import isEqual from 'lodash-es/isEqual'
import isNull from 'lodash-es/isNull'
import mapValues from 'lodash-es/mapValues'

import { getEntityStatuses } from '../components/Entity'
import {
  LABEL_ENTITY_META,
  LABEL_PRIVACY_VALUE_PRIVATE,
  LABEL_PRIVACY_VALUE_PUBLIC,
} from '../components/Label'
import { getShortAddress } from '../utils/address'
import { isValueObjectType } from '../utils/common'
import {
  getDocFlowResultStatusLabelGetter,
  getFloatLabel,
  isFilterNameIdEntity,
} from '../utils/filter'

import { EMPTY_OBJECT } from './common'
import { LABEL_ENTITY, RENTAL_APPLICANT_ENTITY } from './entity'
import {
  DOC_FLOW_RESULT_STATUS_FILTER_LIST,
  FILE_FILTER_LIST,
  USER_FILTER_LIST,
} from './filter'
import { INHABITANT_STATUS } from './inhabitant'
import { RENTAL_PORTAL_APPLICANT_FILTER_LABEL_KEY } from './rentalPortal'
import { ROLE_TRANSLATION_ALIASES } from './user'

function getFileOption({ label }) {
  return {
    label: label.filename,
  }
}

function rentalListingGetterType(option) {
  const flat = option.label?.flat

  return flat
    ? {
        isPersistent: true,
        label: 'RentalPortal.ListingUnitLabelKey',
        tObj: {
          address: getShortAddress(flat.building?.address?.value),
          number: flat.number,
        },
      }
    : commonGetterType(option)
}

const userTypeGetter = option => {
  const { is_persistent: isPersistent, label, value } = option

  const { fullname, is_system: isSystem } = label

  return {
    isPersistent: isPersistent || isNull(value),
    isSystem,
    label: fullname,
  }
}

const roleGetterType = option => {
  const { value } = option

  return {
    isPersistent: true,
    label: ROLE_TRANSLATION_ALIASES[value],
  }
}

// Unlike the entity-dependent `status` getter, the inhabitant status of a
// contract row is always the user profile status.
const inhabitantStatusGetterType = option => {
  const { label, value } = option

  return {
    isPersistent: true,
    label: INHABITANT_STATUS[value]?.text || label,
  }
}

const commonGetterType = option => {
  const { is_persistent, label, value } = option

  if (isNull(value) || is_persistent) {
    return {
      isPersistent: true,
      label,
    }
  }

  return { label }
}

function getRentalApplicantOptionLabelGetter(labelKeys) {
  return (option, entity) =>
    isEqual(entity, RENTAL_APPLICANT_ENTITY) && labelKeys[option.value]
      ? { isPersistent: true, label: labelKeys[option.value] }
      : commonGetterType(option)
}

export const LABEL_GETTER = {
  building: option =>
    commonGetterType({ ...option, label: option.label?.title ?? option.label }),
  common: commonGetterType,
  flat: option =>
    isValueObjectType(option.label)
      ? rentalListingGetterType({ ...option, label: { flat: option.label } })
      : commonGetterType(option),
  inhabitant_role: roleGetterType,
  inhabitant_status: inhabitantStatusGetterType,
  label: option => {
    const { is_persistent, label, value } = option

    const { color, title } = label

    return {
      color,
      isPersistent: is_persistent || value === null,
      label: title,
    }
  },
  rating: option => {
    const { label, value } = option

    return {
      ...(value === null ? EMPTY_OBJECT : { tObj: { count: value } }),
      isPersistent: true,
      label: value === null ? label : 'Filter.StarCountLabelKey',
    }
  },
  request: option => {
    const { is_persistent, label } = option

    return {
      isPersistent: is_persistent,
      label: label.title,
    }
  },
  role: roleGetterType,
  status: (option, entity) => {
    const { label, value } = option

    const text = getEntityStatuses(entity)[value]?.text || label

    return {
      isPersistent: true,
      label: text,
    }
  },
  ...mapValues(
    RENTAL_PORTAL_APPLICANT_FILTER_LABEL_KEY,
    getRentalApplicantOptionLabelGetter
  ),
  ...FILE_FILTER_LIST.reduce((acc, item) => {
    acc[item] = getFileOption

    return acc
  }, {}),
  ...DOC_FLOW_RESULT_STATUS_FILTER_LIST.reduce((acc, item) => {
    acc[item] = getDocFlowResultStatusLabelGetter(item)

    return acc
  }, {}),
  id: (option, entity) => {
    if (isFilterNameIdEntity(entity)) {
      return userTypeGetter(option)
    }

    return rentalListingGetterType(option)
  },
  linked_request: option => {
    const { is_persistent, label } = option

    return {
      isPersistent: is_persistent,
      label: label.title,
    }
  },
  ...USER_FILTER_LIST.reduce((acc, item) => {
    acc[item] = userTypeGetter

    return acc
  }, {}),
  area: option => {
    return {
      label: getFloatLabel(option),
    }
  },
  entity: (option, entity) => {
    if (!isEqual(entity, LABEL_ENTITY)) {
      return commonGetterType(option)
    }

    const row = LABEL_ENTITY_META.find(meta => meta.value === option.value)

    if (!row) {
      return commonGetterType(option)
    }

    return { isPersistent: true, label: row.labelKey }
  },
  file: option => {
    const { is_persistent, label } = option

    return {
      isPersistent: is_persistent,
      label: label.filename,
    }
  },
  live_area: option => {
    return {
      label: getFloatLabel(option),
    }
  },
  privacy: (option, entity) => {
    if (!isEqual(entity, LABEL_ENTITY)) {
      return commonGetterType(option)
    }

    const normalized = option.value?.trim().toLowerCase()

    if (normalized === LABEL_PRIVACY_VALUE_PRIVATE) {
      return { isPersistent: true, label: 'Label.PrivacyFieldPrivateOptionKey' }
    }

    if (normalized === LABEL_PRIVACY_VALUE_PUBLIC) {
      return { isPersistent: true, label: 'Label.PrivacyFieldPublicOptionKey' }
    }

    return commonGetterType(option)
  },
}

export const VALUE_GETTER = {
  common: option => option.value,
}
